import React, { useState, useEffect } from 'react';
import cookie from 'js-cookie';
import Analytics from 'analytics';

import { useAgilyticsState } from '@agilytics/global-state';

import config from './config';
import noopAnalytics from './no-op';

const analyticsConfig = config();
const analyticsLib = Analytics(analyticsConfig);

analyticsLib.disablePlugin('google');

function removeAnalyticsCookies() {
  ['_ga', '_gat', '_gid', '_gali'].forEach(key => {
    cookie.remove(key, { domain: window.location.host.split('.').slice(1).join('.') });
    cookie.remove(key, { domain: window.location.host });
    cookie.remove(key);
  });
}

export const AnalyticsContext = React.createContext();

export function AnalyticsProvider({ children }) {
  const { complianceOptIn } = useAgilyticsState();

  const [timeoutRef, setTimeoutRef] = useState(null);
  const [analytics, setAnalytics] = useState(noopAnalytics(analyticsLib));

  useEffect(() => {
    if (!complianceOptIn) {
      return;
    }

    const hasOptedIn = () => Object.keys(complianceOptIn).includes('analytics');
    if (hasOptedIn()) {
      setTimeoutRef(clearTimeout(timeoutRef));
      setAnalytics(analyticsLib);
      analyticsLib.enablePlugin('google');
    } else {
      analyticsLib.reset();
      analyticsLib.disablePlugin('google');
      setAnalytics(noopAnalytics(analyticsLib));

      // google will desperately try to stick the session
      // so once we are confident it is unloaded, bin
      // the cookies (they'll get the kibosh on next page load
      // anyway!)
      setTimeoutRef(clearTimeout(timeoutRef));
      setTimeoutRef(
        setTimeout(() => {
          if (!hasOptedIn()) {
            removeAnalyticsCookies();
          }
        }, 1500)
      );
    }

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [complianceOptIn, setAnalytics]);

  return (
    <AnalyticsContext.Provider
      value={{
        analytics,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalytics() {
  const context = React.useContext(AnalyticsContext);

  if (context === undefined) {
    throw new Error('useAnalytics must be used within a AnalyticsContext');
  }

  return context;
}
